import * as d3 from 'd3';
import * as moment from 'moment';

import { Crosshair } from '../../pieces/index';
import { VerticalAxis, TimeAxis } from '../../pieces/axes/index';
import { Histogram } from './Histogram';
import { LoggerFactory, ILogger } from '../../../helpers/helpers';
import { infoTextColor } from '../../../../common/constants';

export class Plot {
    private _histogram: Histogram;

    private _xAxis: TimeAxis;
    private _yAxis: VerticalAxis;
    private _seriesGroup: d3.Selection<any>;
    private _areaGroup: d3.Selection<any>;
    private _plotMargins = { marginTop: 10, marginBottom: 30, marginLeft: 60, marginRight: 10 };
    private _data: any[];
    private _extent: [number, number];
    private _crosshair: Crosshair<any>;
    private _seriesName = 'Value:';

    private _xDomain: [string, string];
    private _linesGroup: d3.Selection<any>;
    private _logger: ILogger;
    private _series = [];

    constructor(container: d3.Selection<any>, width: number, height: number) {
        this.init(container, width, height);
        this._logger = LoggerFactory.getLogger('WideTech.Charting.Plot', false, 0);
    }

    private init(container: d3.Selection<any>, width: number, height: number): void {
        const plotMargins = this._plotMargins;
        const group = container.append('g')
            .classed('plot-group', true)
            .attr('transform', `translate(${plotMargins.marginLeft}, ${plotMargins.marginTop})`);

        const plotHeight = Math.max(height - plotMargins.marginBottom - plotMargins.marginTop, 0);
        const plotWidth = Math.max(width - plotMargins.marginLeft - plotMargins.marginRight, 0);

        this._xAxis = new TimeAxis(plotWidth, new Date(), new Date(), 6, group, this._yAxis ? this._yAxis.isAxisVisible() : true);
        this._xAxis.translate(0, plotHeight);

        this._yAxis = new VerticalAxis(plotHeight, 0, 1, 6, group);
        this._yAxis.translate(0, 0);

        this._histogram = new Histogram(group, plotWidth, plotHeight);

        this._linesGroup = group.append('g')
            .classed('lines', true);
        this._seriesGroup = group.append('g')
            .classed('series-group', true);
        this._areaGroup = group.append('g')
            .classed('area-group', true);
        this._areaGroup.append('rect')
            .style('fill', 'rgba(51,153,204,0.2)')
            .attr({
                'x': 0,
                'width': plotWidth
            });

        const ch = group.append('g')
            .classed('crosshair-group', true)
            .classed('zoom-disabled', true);
        this._crosshair = new Crosshair(plotWidth, plotHeight, ch);
        this._crosshair.scale(this._xAxis.scale());
        this._series = [];
        const series = {
            color: () => infoTextColor,
            data: () => false,
            crosshairLabel: (bisect, date, valueFormatter) => {
                let label = '';
                const currentValue = this._data[bisect(this._data, date)];
                if (currentValue) {
                    label = this._seriesName + ': ' + valueFormatter(currentValue.value);
                } else {
                    label = this._seriesName + ': No data';

                }
                return label;
            }
        };
        this._crosshair.addSeries(<any>series);
        this._series.push(series);
    }

    update(data: any[]) {
        this._data = data;
        if (!this._xDomain) {
            const extent = d3.extent(this._data, (d: any) => d.timeStamp);
            this._xAxis.update(extent[0], extent[1]);
        }
        if (this._series) {
            this._series.forEach(s => {
                s.data = () => data;
            });
        }

        const dataBound = this._seriesGroup.selectAll('.series')
            .data(this._data);
        dataBound.exit()
            .remove();
        dataBound.enter()
            .append('circle')
            .classed('series', true)
            .attr('r', 2)
            .style('fill', infoTextColor);
        const xScale = this._xAxis.scale();
        const yScale = this._yAxis.scale();
        this._crosshair.scale(xScale);
        this._crosshair.update(data);
        dataBound.attr({
            'cx': (d: any) => {
                const cx = xScale(d.timeStamp);
                return (cx !== cx) ? 0 : cx;
            },
            'cy': (d: any) => {
                const cy = yScale(d.value);
                return (cy !== cy) ? 0 : cy;
            }
        });

        this._histogram.update(data);
        if (this._extent) {
            this.updateArea(this._extent);
        }
    }

    updateArea(extent: [number, number]): void {
        const yMax: number = this._yAxis.scale()(extent[1]);
        const yMin: number = this._yAxis.scale()(extent[0]);

        // test if value are not NaN since it is not equal to himself
        if (yMin !== yMin || yMax !== yMax) {
            return;
        }

        this._extent = extent;
        this._areaGroup.select('rect')
            .attr({
                'y': Math.max(yMax, 0),
                'height': Math.max(yMin - yMax, 0)
            });
    }

    resize(width: number, height: number) {
        const plotHeight = height - this._plotMargins.marginBottom - this._plotMargins.marginTop;
        const plotWidth = width - this._plotMargins.marginLeft - this._plotMargins.marginRight;

        if (plotHeight !== plotHeight || plotWidth !== plotWidth) {
            return;
        }

        this._xAxis.translate(0, plotHeight);
        this._xAxis.resize(plotWidth);
        this._yAxis.resize(plotHeight);
        this._histogram.resize(plotWidth, plotHeight);
        this._crosshair.resize(plotWidth, plotHeight);
        this.render();
        this._areaGroup.select('rect')
            .attr('width', plotWidth);
        if (this._extent) {
            this.updateArea(this._extent);
        }
    }

    setDomain(domain: [number, number]) {
        this._yAxis.update(domain[0], domain[1]);
        this.render();
        this._histogram.updateRange(domain);
    }

    render() {
        const yScale = this._yAxis.scale();
        if (this._data) {
            this._seriesGroup.selectAll('.series')
                .data(this._data)
                .attr({
                    'cx': d => {
                        const cx = this._xAxis.scale()(moment(d.timeStamp)
                            .toDate());
                        return (cx !== cx) ? 0 : cx;

                    },
                    'cy': d => {
                        const cy = yScale(d.value);
                        return (cy !== cy) ? 0 : cy;
                    }
                });
        }
        const plotWidth = this._xAxis.scale().range()[1];
        this._linesGroup
            .selectAll('.line')
            .select('line')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', d => yScale(d.value))
            .attr('y2', d => yScale(d.value));
    }

    xDomain(xDomain: [string, string]) {
        if (xDomain) {
            this._xDomain = xDomain;
            this._xAxis.update(moment(xDomain[0])
                .toDate(), moment(xDomain[1])
                    .toDate());
            this.render();
        }
    }

    updateLines(lines: { value: number, color: string }[]) {
        try {
            const dataBound = this._linesGroup.selectAll('.line')
                .data(lines);
            dataBound
                .exit()
                .remove();
            const enterSelection = dataBound
                .enter()
                .append('g')
                .classed('line', true);
            enterSelection.append('line');
            dataBound.select('line')
                .style('stroke', d => d.color);
        } catch (e) {
            this._logger.error(e);
        }

    }

    seriesName(value?: string): string {
        if (arguments.length) {
            this._seriesName = value;
        }
        return this._seriesName;
    }

    updateCrosshair(date: Date) {
        this._crosshair.setDate(date);
    }

    onCrosshair(callback) {
        this._crosshair.onCrosshair(callback);
    }

}